<script>
    import {
        CardLayout,
        CustomTooltip,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        calculateTotalFee,
        getAmountInCurrency,
        getBoolean,
        getDisplayTotal,
        getFormatDate,
        getInvoiceStatus,
        getText,
        goToDownloadPage,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        DecimalInput,
        DateInput,
    } from "../../../Components/Common/index.svelte";

    import { editIcon } from "../../../Utils/utilConstants.svelte";

    export let isEditEnabled = false;
    export let isEnableEdit = false;
    export let invoiceDetails = {};
    export let downloadInvoiceType = 1;
    export let patientDetails = {};
    export let isFromAccountPage = false;

    let isUpdatingInvoice = false;

    let defaultInvoiceDetails = {};

    $: referenceName = `${patientDetails.lastName ? patientDetails.lastName.replaceAll(/['",.\-\s]/g, "") : patientDetails.firstName?.replaceAll(/['",.\-\s]/g, "")}${
        invoiceDetails.id
    }`;

    const bottomLinks = [
        {
            displayName: "Download invoice",
            clickFunc: () => downloadInvoice(),
        },
    ];

    const btnOptions = isEnableEdit
        ? [
              {
                  iconName: "editIcon",
                  iFont: editIcon,
                  iconFunction: () => handleEditInvoice(),
                  btnText: "Edit",
              },
          ]
        : [];

    const handleEditInvoice = () => {
        isEditEnabled = true;

        defaultInvoiceDetails = { ...invoiceDetails };
    };

    const updateInvoice = async () => {
        isUpdatingInvoice = true;

        invoiceDetails.totalFee =
            invoiceDetails.actualGap +
            invoiceDetails.internalProcessingFee +
            invoiceDetails.rebate;

        await apiMethods.putMethod
            .updateInvoice(invoiceDetails)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update invoice details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else _handleUpdateInvoice(res);
            })
            .catch((err) => showError(err));

        isEditEnabled = false;

        isUpdatingInvoice = false;
    };

    const cancelEditInvoice = () => {
        invoiceDetails = { ...defaultInvoiceDetails };
        isEditEnabled = false;
    };

    const downloadInvoice = () => {
        goToDownloadPage(downloadInvoiceType, invoiceDetails.id);
    };

    // HELPER FUNCTIONS
    const _handleUpdateInvoice = (res) => {
        invoiceDetails = { ...res };
        defaultInvoiceDetails = { ...res };
    };
</script>

<CardLayout title="Invoice Details" {btnOptions} {bottomLinks}>
    <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Invoice No.:</p>
                <p class="content">
                    {getText(invoiceDetails.invoiceNumber)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Status:</p>
                <p
                    class={`content ${invoiceDetails.status ? ([1, 2].includes(invoiceDetails.status) ? "text-[#2DBD6F]" : [9].includes(invoiceDetails.status) ? "text-[#E05757]" : "") : ""}`}
                >
                    {getInvoiceStatus(invoiceDetails.status)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Created Date:</p>
                <p class="content">
                    {getFormatDate(invoiceDetails.createdDate)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">
                    {downloadInvoiceType == 14 ? "Updated Date" : "Due Date"}:
                </p>

                {#if downloadInvoice == 14}
                    <p class="content">
                        {getFormatDate(invoiceDetails.updatedDate)}
                    </p>
                {:else if !isEditEnabled}
                    <p class="content">
                        {getFormatDate(invoiceDetails.dueDate)}
                    </p>
                {:else}
                    <DateInput
                        bind:value={invoiceDetails.dueDate}
                        wrapperClass="input input-sm px-0 input-bordered"
                        wrapperClassFocus="inputFocus"
                    />
                {/if}
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            {#if isFromAccountPage}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Subtotal:</p>
                    <p class="content">
                        {getAmountInCurrency(invoiceDetails.totalFee)}
                    </p>
                </div>
                <div class="flex flex-row gap-x-2">
                    <p class="title">GST:</p>
                    <p class="content">
                        {invoiceDetails.addGST
                            ? getAmountInCurrency(invoiceDetails.totalFee * 0.1)
                            : "$0"}
                    </p>
                </div>
            {:else}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Add GST:</p>
                    <p class="content">{getBoolean(invoiceDetails.addGST)}</p>
                </div>
            {/if}

            {#if downloadInvoiceType != 5 && downloadInvoiceType != 14}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Actual Gap:</p>
                    <p class="content">
                        {getAmountInCurrency(invoiceDetails.actualGap)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <p class="title">Rebate:</p>
                    <p class="content">
                        {getAmountInCurrency(invoiceDetails.rebate)}
                    </p>
                </div>

                <div class="flex flex-row gap-x-2">
                    <div class="title flex items-center gap-x-2">
                        <p>Processing Fee:</p>

                        <CustomTooltip>
                            <i
                                class="fa-regular fa-circle-question"
                                slot="tooltipTrigger"
                            />

                            <p>Internal Processing Fee</p>
                        </CustomTooltip>
                    </div>
                    {#if !isEditEnabled}
                        <p class="content">
                            {getAmountInCurrency(
                                invoiceDetails.internalProcessingFee,
                            )}
                        </p>
                    {:else}
                        <DecimalInput
                            inputClass="content bg-[#e5e7eb]"
                            bind:value={invoiceDetails.internalProcessingFee}
                            on:change={calculateTotalFee(invoiceDetails)}
                        />
                    {/if}
                </div>
            {/if}

            <div class="flex flex-row gap-x-2">
                <p class="title">Total:</p>
                <p class="content">
                    {getDisplayTotal(invoiceDetails)}
                </p>
            </div>
        </div>

        {#if !isFromAccountPage}
            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-full">
                <div class="flex flex-row gap-x-2">
                    <p class="title">Reference:</p>
                    <p class="content">
                        {referenceName}
                    </p>
                </div>

                {#if isEditEnabled}
                    <div class="flex flex-1 items-end justify-end gap-x-2">
                        <button
                            class={`btn btn-sm btn-error w-24 ${
                                isUpdatingInvoice ? "loading" : ""
                            }`}
                            on:click|preventDefault={cancelEditInvoice}
                            disabled={isUpdatingInvoice}
                        >
                            Cancel
                        </button>
                        <button
                            class={`btn btn-sm btn-secondary w-24 ${
                                isUpdatingInvoice ? "loading w-28" : ""
                            }`}
                            on:click|preventDefault={updateInvoice}
                            disabled={isUpdatingInvoice}
                        >
                            {isUpdatingInvoice ? "Saving" : "Save"}
                        </button>
                    </div>
                {/if}
            </div>
        {/if}
    </div>
</CardLayout>

<style>
    .title {
        width: 200px;
    }

    .content {
        width: calc(100% - 200px);
    }
</style>
